import { getStoredSessionToken } from '../../../app/services/session.service';

const SUPPLIERS_API_BASE_URL = (process.env.REACT_APP_SUPPLIERS_API_BASE_URL || '').replace(/\/$/, '');
const SUPPLIERS_API_PATH = '/api/suppliers';
const TENANT_DB_ERROR_CODES = new Set([
  'tenant_database_not_ready',
  'tenant_database_unavailable',
  'tenant_database_metadata_missing',
]);
const AUTH_ERRORS = new Set(['SUPPLIERS_AUTH_REQUIRED', 'SUPPLIERS_SESSION_REFRESH_REQUIRED', 'SUPPLIERS_SESSION_EXPIRED']);

function buildResult({ ok, data = null, error = null, message = '' }) {
  return { ok, data, error, message };
}

function getSuppliersApiUrl(path = '') {
  return `${SUPPLIERS_API_BASE_URL}${path || SUPPLIERS_API_PATH}`;
}

function getSuppliersAuthHeaders(extraHeaders = {}) {
  const token = getStoredSessionToken();
  return token ? { ...extraHeaders, Authorization: `Bearer ${token}` } : extraHeaders;
}

async function readPayload(response) {
  try {
    return await response.clone().json();
  } catch {
    return null;
  }
}

function mapApiSupplierToSupplier(supplier = {}) {
  return {
    id: supplier.id,
    legalName: supplier.legalName || '',
    displayName: supplier.displayName || supplier.legalName || '',
    category: supplier.category || '',
    ownerName: supplier.ownerName || '',
    reaNumber: supplier.reaNumber || '',
    referredBy: supplier.referredBy || '',
    sector: supplier.sector || '',
    legalForm: supplier.legalForm || '',
    vatNumber: supplier.vatNumber || '',
    taxCode: supplier.taxCode || '',
    registeredAddress: supplier.registeredAddress || '',
    companyEmail: supplier.companyEmail || '',
    companyPhone: supplier.companyPhone || '',
    contactName: supplier.contactName || '',
    contactRole: supplier.contactRole || '',
    contactEmail: supplier.contactEmail || '',
    contactPhone: supplier.contactPhone || '',
    status: supplier.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
    notes: supplier.notes || '',
    ...(supplier.customFields ? { customFields: supplier.customFields } : {}),
  };
}

function mapApiSupplierNoteToComment(note = {}) {
  const request = {
    id: note.id,
    supplierId: note.supplierId,
    parentId: note.parentId ?? null,
    authorUserId: note.authorUserId || '',
    authorName: note.authorName || '',
    body: note.body || '',
    createdAt: note.createdAt || null,
  };
  return request;
}

function normalizeOptional(value) {
  return value == null ? '' : String(value).trim();
}

function buildSupplierRequest(payload = {}) {
  const request = {
    legalName: normalizeOptional(payload.legalName || payload.displayName),
    displayName: normalizeOptional(payload.displayName || payload.legalName),
    category: normalizeOptional(payload.category),
    sector: normalizeOptional(payload.sector),
    legalForm: normalizeOptional(payload.legalForm),
    vatNumber: normalizeOptional(payload.vatNumber),
    taxCode: normalizeOptional(payload.taxCode),
    registeredAddress: normalizeOptional(payload.registeredAddress),
    companyEmail: normalizeOptional(payload.companyEmail),
    companyPhone: normalizeOptional(payload.companyPhone),
    contactName: normalizeOptional(payload.contactName),
    contactRole: normalizeOptional(payload.contactRole),
    contactEmail: normalizeOptional(payload.contactEmail),
    contactPhone: normalizeOptional(payload.contactPhone),
    notes: normalizeOptional(payload.notes),
  };

  for (const field of ['ownerName', 'reaNumber', 'referredBy']) {
    if (Object.prototype.hasOwnProperty.call(payload, field)) request[field] = normalizeOptional(payload[field]);
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'customFields')) {
    request.customFields = payload.customFields;
  }

  return request;
}

function mapError(status, payload, fallbackError) {
  if (payload?.error === 'module_disabled') {
    return buildResult({ ok: false, data: [], error: 'SUPPLIERS_MODULE_DISABLED', message: 'Modulo fornitori non abilitato per questo workspace.' });
  }

  if (TENANT_DB_ERROR_CODES.has(payload?.error)) {
    return buildResult({ ok: false, data: [], error: 'SUPPLIERS_TENANT_DATABASE_UNAVAILABLE', message: payload?.message || 'Database tenant non disponibile per il modulo fornitori.' });
  }

  if (status === 401) {
    return buildResult({ ok: false, data: [], error: 'SUPPLIERS_AUTH_REQUIRED', message: payload?.message || 'Sessione non autorizzata per il catalogo fornitori.' });
  }

  if (status === 403) {
    return buildResult({ ok: false, data: [], error: 'SUPPLIERS_PERMISSION_DENIED', message: payload?.message || 'Permessi insufficienti.' });
  }

  if (status === 409) {
    return buildResult({ ok: false, data: [], error: fallbackError || 'SUPPLIERS_HAS_RELATED_RECORDS', message: 'Il fornitore ha dati collegati e non può essere eliminato. Puoi disattivarlo dalla sua card.' });
  }

  return buildResult({ ok: false, data: [], error: fallbackError || `SUPPLIERS_API_FAILED_${status}`, message: 'Backend fornitori non disponibile.' });
}

export async function listSuppliersResult() {
  try {
    const response = await fetch(getSuppliersApiUrl(SUPPLIERS_API_PATH), {
      headers: getSuppliersAuthHeaders(),
    });
    const payload = await readPayload(response);

    if (!response.ok) {
      return mapError(response.status, payload, `SUPPLIERS_LIST_FAILED_${response.status}`);
    }

    const successPayload = Array.isArray(payload) ? payload : await response.json();
    if (!Array.isArray(successPayload)) {
      return buildResult({ ok: false, data: [], error: 'SUPPLIERS_LIST_INVALID_PAYLOAD', message: 'Payload fornitori non valido.' });
    }

    return buildResult({ ok: true, data: successPayload.map(mapApiSupplierToSupplier) });
  } catch {
    return buildResult({ ok: false, data: [], error: 'SUPPLIERS_API_UNAVAILABLE', message: 'Backend fornitori non disponibile.' });
  }
}

export async function getSupplierResult(supplierId) {
  try {
    const response = await fetch(getSuppliersApiUrl(`${SUPPLIERS_API_PATH}/${supplierId}`), {
      headers: getSuppliersAuthHeaders(),
    });
    const payload = await readPayload(response);

    if (!response.ok) {
      return mapError(response.status, payload, `SUPPLIERS_DETAIL_FAILED_${response.status}`);
    }

    return buildResult({ ok: true, data: mapApiSupplierToSupplier(payload) });
  } catch {
    return buildResult({ ok: false, error: 'SUPPLIERS_API_UNAVAILABLE', message: 'Backend fornitori non disponibile.' });
  }
}

export async function createSupplier(payload) {
  return mutateSupplier(SUPPLIERS_API_PATH, 'POST', payload);
}

export async function updateSupplier(supplierId, payload) {
  return mutateSupplier(`${SUPPLIERS_API_PATH}/${supplierId}`, 'PUT', payload);
}

export async function archiveSupplier(supplierId) {
  try {
    const response = await fetch(getSuppliersApiUrl(`${SUPPLIERS_API_PATH}/${supplierId}/archive`), {
      method: 'POST',
      headers: getSuppliersAuthHeaders(),
    });
    const payload = await readPayload(response);
    if (!response.ok) {
      return mapError(response.status, payload, `SUPPLIERS_ARCHIVE_FAILED_${response.status}`);
    }
    return buildResult({ ok: true });
  } catch {
    return buildResult({ ok: false, error: 'SUPPLIERS_API_UNAVAILABLE', message: 'Backend fornitori non disponibile.' });
  }
}

export async function restoreSupplier(supplierId) {
  try {
    const response = await fetch(getSuppliersApiUrl(`${SUPPLIERS_API_PATH}/${supplierId}/restore`), {
      method: 'POST',
      headers: getSuppliersAuthHeaders(),
    });
    const payload = await readPayload(response);
    if (!response.ok) {
      return mapError(response.status, payload, `SUPPLIERS_RESTORE_FAILED_${response.status}`);
    }
    return buildResult({ ok: true, data: { supplier: mapApiSupplierToSupplier(payload) } });
  } catch {
    return buildResult({ ok: false, error: 'SUPPLIERS_API_UNAVAILABLE', message: 'Backend fornitori non disponibile.' });
  }
}

export async function deleteSupplierPermanently(supplierId) {
  try {
    const response = await fetch(getSuppliersApiUrl(`${SUPPLIERS_API_PATH}/${supplierId}`), { method: 'DELETE', headers: getSuppliersAuthHeaders() });
    const payload = await readPayload(response);
    if (!response.ok) return mapError(response.status, payload, `SUPPLIERS_DELETE_FAILED_${response.status}`);
    return buildResult({ ok: true });
  } catch {
    return buildResult({ ok: false, error: 'SUPPLIERS_API_UNAVAILABLE', message: 'Backend fornitori non disponibile.' });
  }
}

export async function listSupplierNotesResult(supplierId) {
  try {
    const response = await fetch(getSuppliersApiUrl(`${SUPPLIERS_API_PATH}/${supplierId}/notes`), {
      headers: getSuppliersAuthHeaders(),
    });
    const payload = await readPayload(response);
    if (!response.ok) {
      return mapError(response.status, payload, `SUPPLIERS_NOTES_LIST_FAILED_${response.status}`);
    }
    const notes = Array.isArray(payload) ? payload : [];
    return buildResult({ ok: true, data: notes.map(mapApiSupplierNoteToComment) });
  } catch {
    return buildResult({ ok: false, data: [], error: 'SUPPLIERS_API_UNAVAILABLE', message: 'Commenti fornitore non disponibili.' });
  }
}

export async function createSupplierNoteResult(supplierId, body, parentId = null) {
  try {
    const response = await fetch(getSuppliersApiUrl(`${SUPPLIERS_API_PATH}/${supplierId}/notes`), {
      method: 'POST',
      headers: getSuppliersAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        body: normalizeOptional(body),
        ...(parentId !== null && parentId !== undefined ? { parentId } : {}),
      }),
    });
    const payload = await readPayload(response);
    if (!response.ok) {
      return mapError(response.status, payload, `SUPPLIERS_NOTES_CREATE_FAILED_${response.status}`);
    }
    return buildResult({ ok: true, data: mapApiSupplierNoteToComment(payload) });
  } catch {
    return buildResult({ ok: false, error: 'SUPPLIERS_API_UNAVAILABLE', message: 'Commento fornitore non salvato.' });
  }
}

export async function deleteSupplierNoteResult(supplierId, noteId) {
  try {
    const response = await fetch(getSuppliersApiUrl(`${SUPPLIERS_API_PATH}/${supplierId}/notes/${noteId}`), {
      method: 'DELETE',
      headers: getSuppliersAuthHeaders(),
    });
    const payload = await readPayload(response);
    if (!response.ok) {
      return mapError(response.status, payload, `SUPPLIERS_NOTES_DELETE_FAILED_${response.status}`);
    }
    return buildResult({ ok: true });
  } catch {
    return buildResult({ ok: false, error: 'SUPPLIERS_API_UNAVAILABLE', message: 'Commento fornitore non eliminato.' });
  }
}

async function mutateSupplier(path, method, payload) {
  try {
    const response = await fetch(getSuppliersApiUrl(path), {
      method,
      headers: getSuppliersAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(buildSupplierRequest(payload)),
    });
    const responsePayload = await readPayload(response);

    if (response.status === 400) {
      return buildResult({ ok: false, error: 'SUPPLIER_VALIDATION_ERROR', message: 'Compila i campi obbligatori del fornitore.' });
    }
    if (!response.ok) {
      return mapError(response.status, responsePayload, `SUPPLIERS_MUTATION_FAILED_${response.status}`);
    }
    return buildResult({ ok: true, data: { supplier: mapApiSupplierToSupplier(responsePayload) } });
  } catch {
    return buildResult({ ok: false, error: 'SUPPLIERS_API_UNAVAILABLE', message: 'Backend fornitori non disponibile.' });
  }
}

export { AUTH_ERRORS as SUPPLIERS_AUTH_REQUIRED_ERRORS };

const CUSTOM_FIELDS_PATH = `${SUPPLIERS_API_PATH}/custom-fields`;

export async function listSupplierCustomFields() {
  const response = await fetch(getSuppliersApiUrl(CUSTOM_FIELDS_PATH), { headers: getSuppliersAuthHeaders() });
  const payload = await readPayload(response);
  if (!response.ok) throw new Error(payload?.message || 'Impossibile caricare i campi personalizzati.');
  return payload;
}

export async function createSupplierCustomField(definition) {
  const response = await fetch(getSuppliersApiUrl(CUSTOM_FIELDS_PATH), { method: 'POST', headers: getSuppliersAuthHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify(definition) });
  const payload = await readPayload(response);
  if (!response.ok) throw new Error(payload?.message || 'Impossibile salvare il campo personalizzato.');
  return payload;
}

export async function archiveSupplierCustomField(id) {
  const response = await fetch(getSuppliersApiUrl(`${CUSTOM_FIELDS_PATH}/${id}`), { method: 'DELETE', headers: getSuppliersAuthHeaders() });
  const payload = await readPayload(response);
  if (!response.ok) throw new Error(payload?.message || 'Impossibile archiviare il campo personalizzato.');
}
